import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../hooks/useTheme';
import { getAlgoStatus, startAlgo, stopAlgo } from '../services/apiClient';
import { numerologyTradingClient } from '../services/numerologyTradingClient';
import Card, { CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from './ui/Card';
import Button from './ui/Button';
import { theme } from '../styles/design-system';
import WorkflowExplanation from './WorkflowExplanation';

const AdvancedAlgoRunnerPage = () => {
  const { isAuthenticated, user } = useAuth();
  const { isDark } = useTheme();
  const [algoStatus, setAlgoStatus] = useState('unknown');
  const [showWorkflow, setShowWorkflow] = useState(false);
  const [algoBusy, setAlgoBusy] = useState(false);
  const [logs, setLogs] = useState([]);
  const [isLogging, setIsLogging] = useState(false);
  const [strategyStatus, setStrategyStatus] = useState(null);
  const [performanceMetrics, setPerformanceMetrics] = useState(null);
  const [topSymbols, setTopSymbols] = useState([]);
  const [topSymbolsLoading, setTopSymbolsLoading] = useState(false);
  const statusIntervalRef = useRef(null);

  // Advanced Configuration State
  const [algoForm, setAlgoForm] = useState({
    // Basic Parameters
    api_key: '94b1jpx5tuvjn6sg', // Default Zerodha API key from your .env
    access_token: '', // Will be loaded from environment or user input
    symbol: 'RELIANCE',
    poll_secs: 30,
    paper: true,
    data_source: 'live',
    session_id: '',

    // Advanced Numerology Parameters
    user_id: user?.id?.toString() || '1',
    min_compatibility_score: 0.60,
    signal_confidence_threshold: 0.65,
    numerology_api_url: process.env.REACT_APP_API_URL || 'http://localhost:8000',

    // Multi-Symbol Trading
    max_symbols_analyze: 50,
    enable_multi_symbol: true,
    max_concurrent_positions: 3,

    // Position Sizing
    position_sizing_method: 'kelly',
    max_position_per_symbol: 0.1,

    // Risk Management
    stop_loss_percentage: 0.02,
    take_profit_percentage: 0.04,
    rebalance_interval: 300,

    // Advanced Features
    volatility_adjustment: true,
    market_hours_only: true,
    enable_sector_rotation: true,
    risk_free_rate: 0.06
  });

  // Load top symbols and access token on component mount
  useEffect(() => {
    loadTopSymbols();
    loadAccessToken();
  }, []);

  const loadAccessToken = async () => {
    try {
      // Try to get credentials from backend environment
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const response = await fetch(`${API_URL}/algo/get-access-token`);
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.access_token && data.api_key) {
          setAlgoForm(prev => ({
            ...prev,
            access_token: data.access_token,
            api_key: data.api_key
          }));
          addLog('✅ Zerodha credentials loaded from backend', 'success');
        } else {
          addLog('⚠️ Please provide API Key and Access Token to start the algo.', 'warning');
          addLog('ℹ️ 📋 To get your access token:', 'info');
          addLog('ℹ️ 1. Go to backend folder: cd backend', 'info');
          addLog('ℹ️ 2. Run: python3 generate_access_token.py', 'info');
          addLog('ℹ️ 3. Follow the instructions to get your access token', 'info');
          addLog('ℹ️ 4. Click the "Load" button to automatically load it', 'info');
        }
      }
    } catch (error) {
      addLog('⚠️ Could not connect to backend. Please ensure server is running on port 8000.', 'warning');
    }
  };

  const testConnection = async () => {
    if (!algoForm.api_key || !algoForm.access_token) {
      addLog('⚠️ Please provide both API Key and Access Token to test connection.', 'warning');
      return;
    }

    addLog('🔍 Testing Zerodha connection...', 'info');
    try {
      const response = await fetch('http://localhost:8002/test-live-trading');
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          addLog('✅ Connection test successful!', 'success');
          addLog(`👤 User: ${data.user.name} (${data.user.email})`, 'info');
          addLog(`💰 Available Cash: ₹${data.account.available_cash}`, 'info');
          addLog(`📊 Total Balance: ₹${data.account.total_balance}`, 'info');
        } else {
          addLog(`❌ Connection test failed: ${data.message}`, 'error');
        }
      } else {
        addLog('❌ Connection test failed. Please check your credentials.', 'error');
      }
    } catch (error) {
      addLog('❌ Connection test failed. Please ensure test endpoint is running on port 8002.', 'error');
    }
  };

  // Status monitoring and Log Polling
  useEffect(() => {
    if (algoStatus === 'running') {
      statusIntervalRef.current = setInterval(() => {
        fetchStrategyStatus();
        fetchBackendLogs();
      }, 2000); // Poll every 2 seconds for logs
    } else {
      if (statusIntervalRef.current) {
        clearInterval(statusIntervalRef.current);
        statusIntervalRef.current = null;
      }
    }
    return () => {
      if (statusIntervalRef.current) {
        clearInterval(statusIntervalRef.current);
      }
    };
  }, [algoStatus]);

  const fetchBackendLogs = async () => {
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const response = await fetch(`${API_URL}/algo/logs?limit=20`);
      if (response.ok) {
        const data = await response.json();
        // Merge new logs with existing logs, avoiding duplicates based on timestamp+message
        if (data.logs && data.logs.length > 0) {
          setLogs(prevLogs => {
            const newLogs = data.logs.filter(newLog =>
              !prevLogs.some(prevLog =>
                prevLog.timestamp === newLog.timestamp && prevLog.message === newLog.message
              )
            ).map(log => ({
              message: log.message,
              type: log.level === 'ERROR' ? 'error' : log.level === 'WARNING' ? 'warning' : 'info',
              timestamp: new Date(log.timestamp).toLocaleTimeString()
            }));

            if (newLogs.length === 0) return prevLogs;
            return [...prevLogs, ...newLogs].slice(-100); // Keep last 100 logs
          });
        }
      }
    } catch (error) {
      console.error('Error fetching logs:', error);
    }
  };

  const loadTopSymbols = async () => {
    setTopSymbolsLoading(true);
    try {
      const response = await numerologyTradingClient.getTopSymbols(20);
      setTopSymbols(response.data.symbols);
      addLog(`📊 Loaded ${response.data.symbols.length} top symbols`, 'success');
    } catch (error) {
      addLog(`❌ Failed to load top symbols: ${error.message}`, 'error');
    } finally {
      setTopSymbolsLoading(false);
    }
  };

  const fetchStrategyStatus = async () => {
    try {
      const res = await getAlgoStatus();
      if (res.data.strategy_status) {
        setStrategyStatus(res.data.strategy_status);
        setPerformanceMetrics(res.data.strategy_status.performance_metrics);
      }
    } catch (error) {
      console.error('Error fetching strategy status:', error);
    }
  };

  const handleStartAlgo = async () => {
    if (!algoForm.api_key || !algoForm.access_token) {
      addLog('⚠️ Please provide API Key and Access Token to start the algo.', 'warning');
      addLog('📝 To get your access token:', 'info');
      addLog('1. Go to backend folder: cd backend', 'info');
      addLog('2. Run: python3 generate_access_token.py', 'info');
      addLog('3. Follow the instructions to get your access token', 'info');
      addLog('4. Click the "Load" button to automatically load it', 'info');
      return;
    }

    setAlgoBusy(true);
    addLog('🚀 Starting Advanced Numerology Algo Runner...', 'info');
    addLog(`📊 Configuration: ${algoForm.position_sizing_method} sizing, ${algoForm.max_symbols_analyze} symbols, ${algoForm.enable_multi_symbol ? 'Multi-symbol' : 'Single-symbol'} mode`, 'info');

    try {
      // Prepare the payload with all advanced parameters
      const payload = {
        // Basic parameters
        api_key: algoForm.api_key,
        access_token: algoForm.access_token,
        symbol: algoForm.symbol,
        poll_secs: algoForm.poll_secs,
        paper: algoForm.paper,
        data_source: algoForm.data_source,
        session_id: algoForm.session_id,

        // Advanced numerology parameters
        user_id: algoForm.user_id,
        min_compatibility_score: algoForm.min_compatibility_score,
        signal_confidence_threshold: algoForm.signal_confidence_threshold,
        numerology_api_url: algoForm.numerology_api_url,

        // Multi-symbol trading
        max_symbols_analyze: algoForm.max_symbols_analyze,
        enable_multi_symbol: algoForm.enable_multi_symbol,
        max_concurrent_positions: algoForm.max_concurrent_positions,

        // Position sizing
        position_sizing_method: algoForm.position_sizing_method,
        max_position_per_symbol: algoForm.max_position_per_symbol,

        // Risk management
        stop_loss_percentage: algoForm.stop_loss_percentage,
        take_profit_percentage: algoForm.take_profit_percentage,
        rebalance_interval: algoForm.rebalance_interval,

        // Advanced features
        volatility_adjustment: algoForm.volatility_adjustment,
        market_hours_only: algoForm.market_hours_only,
        enable_sector_rotation: algoForm.enable_sector_rotation,
        risk_free_rate: algoForm.risk_free_rate
      };

      await startAlgo(payload);
      setAlgoStatus('running');
      addLog('✅ Advanced Algo Runner started successfully!', 'success');
      addLog('🔮 Numerology analysis active with real-time market data', 'info');
      addLog(`🎯 Analyzing ${algoForm.max_symbols_analyze} symbols with ${algoForm.position_sizing_method} position sizing`, 'info');
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.response?.data?.message || error.message;
      addLog(`❌ Failed to start algo: ${errorMessage}`, 'error');
      console.error('Algo start error:', error);
    } finally {
      setAlgoBusy(false);
    }
  };

  const handleStopAlgo = async () => {
    setAlgoBusy(true);
    addLog('🛑 Stopping Advanced Algo Runner...', 'info');

    try {
      await stopAlgo();
      setAlgoStatus('stopped');
      setStrategyStatus(null);
      setPerformanceMetrics(null);
      addLog('✅ Advanced Algo Runner stopped successfully!', 'success');
    } catch (error) {
      addLog(`❌ Failed to stop algo: ${error.response?.data?.detail || error.message}`, 'error');
    } finally {
      setAlgoBusy(false);
    }
  };

  const addLog = (message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs(prev => [...prev, { message, type, timestamp }]);
  };

  const clearLogs = () => {
    setLogs([]);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'running': return theme.colors.success[500];
      case 'stopped': return theme.colors.error[500];
      default: return theme.colors.gray[500];
    }
  };

  const getLogColor = (type) => {
    switch (type) {
      case 'success': return theme.colors.success[500];
      case 'error': return theme.colors.error[500];
      case 'warning': return theme.colors.warning[500];
      default: return theme.colors.gray[600];
    }
  };

  const formatNumber = (num, decimals = 2) => {
    if (num === null || num === undefined) return 'N/A';
    return typeof num === 'number' ? num.toFixed(decimals) : num;
  };

  const formatPercentage = (num) => {
    if (num === null || num === undefined) return 'N/A';
    return typeof num === 'number' ? `${(num * 100).toFixed(2)}%` : num;
  };

  if (!isAuthenticated) {
    return (
      <div style={styles.authPrompt}>
        <h2 style={styles.authTitle}>🔐 Authentication Required</h2>
        <p style={styles.authMessage}>
          Please login to access the Advanced Algo Runner functionality.
        </p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <WorkflowExplanation
        isOpen={showWorkflow}
        onClose={() => setShowWorkflow(false)}
        type="algo"
      />
      {/* Modern Hero Section */}
      <div style={styles.heroSection}>
        <div style={styles.heroOverlay} />
        <div style={styles.heroContent}>
          <div style={styles.heroHeader}>
            <span style={styles.heroIcon}>🤖</span>
            <div>
              <h1 style={styles.heroTitle}>Advanced Algo Runner</h1>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                <p style={styles.heroSubtitle}>
                  AI-powered numerology trading engine
                </p>
                <button
                  onClick={() => setShowWorkflow(true)}
                  style={{
                    background: 'rgba(255,255,255,0.2)',
                    border: '1px solid rgba(255,255,255,0.3)',
                    borderRadius: '20px',
                    padding: '4px 12px',
                    color: 'white',
                    fontSize: '12px',
                    cursor: 'pointer',
                    backdropFilter: 'blur(4px)',
                    fontWeight: '500',
                    transition: 'all 0.2s'
                  }}
                  onMouseOver={(e) => e.target.style.background = 'rgba(255,255,255,0.3)'}
                  onMouseOut={(e) => e.target.style.background = 'rgba(255,255,255,0.2)'}
                >
                  ℹ️ How it Works?
                </button>
              </div>
            </div>
          </div>

          {/* Quick Status Badge */}
          <div style={{
            ...styles.statusBadge,
            backgroundColor: algoStatus === 'running' ? 'rgba(255, 255, 255, 0.95)' : 'rgba(255, 255, 255, 0.95)',
            borderColor: algoStatus === 'running' ? '#10b981' : '#ef4444',
            color: algoStatus === 'running' ? '#10b981' : '#ef4444',
          }}>
            <div style={{
              ...styles.statusDot,
              backgroundColor: algoStatus === 'running' ? '#10b981' : '#ef4444',
              animation: algoStatus === 'running' ? 'pulse 2s infinite' : 'none'
            }} />
            {algoStatus === 'running' ? 'SYSTEM ACTIVE' : 'SYSTEM STOPPED'}
          </div>
        </div>
      </div>

      <div style={styles.content}>
        {/* Control Center */}
        <div style={styles.mainGrid}>
          <Card variant="elevated" padding="lg" style={styles.controlCard}>
            <CardHeader>
              <CardTitle>🚀 Mission Control</CardTitle>
              <CardDescription>Start or stop your trading algorithm</CardDescription>
            </CardHeader>
            <CardContent>
              <div style={styles.controlActions}>
                <Button
                  variant="primary"
                  onClick={handleStartAlgo}
                  disabled={algoBusy || algoStatus === 'running'}
                  loading={algoBusy && algoStatus !== 'running'}
                  icon="▶️"
                  style={{
                    ...styles.controlButton,
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    opacity: algoStatus === 'running' ? 0.5 : 1
                  }}
                >
                  INITIALIZE ALGO
                </Button>
                <Button
                  variant="danger"
                  onClick={handleStopAlgo}
                  disabled={algoBusy || algoStatus !== 'running'}
                  loading={algoBusy && algoStatus === 'running'}
                  icon="⏹️"
                  style={{
                    ...styles.controlButton,
                    background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                    opacity: algoStatus !== 'running' ? 0.5 : 1
                  }}
                >
                  TERMINATE ALGO
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Terminal Logs */}
          <Card variant="elevated" padding="none" style={styles.terminalCard}>
            <div style={styles.terminalHeader}>
              <div style={styles.terminalTitle}>
                <span style={styles.terminalIcon}>_&gt;</span>
                System Logs
              </div>
              <button onClick={clearLogs} style={styles.clearButton}>CLEAR</button>
            </div>
            <div style={styles.terminalBody}>
              {logs.length === 0 ? (
                <div style={styles.emptyTerminal}>
                  <span style={{ opacity: 0.5 }}>&gt; System ready. Waiting for command...</span>
                </div>
              ) : (
                logs.map((log, index) => (
                  <div key={index} style={styles.logLine}>
                    <span style={styles.logTime}>[{log.timestamp}]</span>
                    <span style={{
                      color: log.type === 'error' ? '#ef4444' :
                        log.type === 'success' ? '#10b981' :
                          log.type === 'warning' ? '#f59e0b' : '#e5e7eb'
                    }}>
                      {log.type === 'info' && 'ℹ️ '}
                      {log.type === 'success' && '✅ '}
                      {log.type === 'error' && '❌ '}
                      {log.type === 'warning' && '⚠️ '}
                      {log.message}
                    </span>
                  </div>
                ))
              )}
            </div>
          </Card>

          {/* Live Metrics */}
          {performanceMetrics && (
            <Card variant="elevated" padding="lg" style={styles.metricsCard}>
              <CardHeader>
                <CardTitle>📊 Live Performance</CardTitle>
              </CardHeader>
              <CardContent>
                <div style={styles.metricsGrid}>
                  <div style={styles.metricBox}>
                    <span style={styles.metricLabel}>Total P&L</span>
                    <span style={{
                      ...styles.metricValue,
                      color: performanceMetrics.total_pnl >= 0 ? '#10b981' : '#ef4444'
                    }}>
                      {formatNumber(performanceMetrics.total_pnl)}
                    </span>
                  </div>
                  <div style={styles.metricBox}>
                    <span style={styles.metricLabel}>Win Rate</span>
                    <span style={styles.metricValue}>{formatPercentage(performanceMetrics.win_rate)}</span>
                  </div>
                  <div style={styles.metricBox}>
                    <span style={styles.metricLabel}>Trades</span>
                    <span style={styles.metricValue}>{performanceMetrics.total_trades || 0}</span>
                  </div>
                  <div style={styles.metricBox}>
                    <span style={styles.metricLabel}>Sharpe</span>
                    <span style={styles.metricValue}>{formatNumber(performanceMetrics.sharpe_ratio)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Configuration Panel */}
        <Card variant="elevated" padding="none" style={styles.configCard}>
          <div style={styles.configHeader}>
            <h3 style={styles.configTitle}>⚙️ System Configuration</h3>
            <div style={styles.tabs}>
              {['Basic', 'Numerology', 'Risk', 'Advanced'].map((tab) => (
                <button
                  key={tab}
                  style={styles.tabButton} // Simplified for now, logic would need state
                >
                  {tab}
                </button>
              ))}
            </div>
          </div>

          <div style={styles.configContent}>
            {/* Basic Settings Grid */}
            <div style={styles.configGrid}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Target Symbol</label>
                <select
                  value={algoForm.symbol}
                  onChange={(e) => setAlgoForm(prev => ({ ...prev, symbol: e.target.value }))}
                  style={styles.modernSelect}
                >
                  <option value="RELIANCE">RELIANCE</option>
                  <option value="TCS">TCS</option>
                  <option value="INFY">INFY</option>
                  <option value="HDFC">HDFC</option>
                  {topSymbols.slice(0, 5).map(symbol => (
                    <option key={symbol.symbol} value={symbol.symbol}>{symbol.symbol}</option>
                  ))}
                </select>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Capital Allocation</label>
                <input
                  type="number"
                  value={algoForm.max_position_per_symbol}
                  onChange={(e) => setAlgoForm(prev => ({ ...prev, max_position_per_symbol: parseFloat(e.target.value) }))}
                  style={styles.modernInput}
                  placeholder="0.1"
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Strategy Mode</label>
                <div style={styles.toggleContainer}>
                  <label style={styles.toggleLabel}>
                    <input
                      type="checkbox"
                      checked={algoForm.paper}
                      onChange={(e) => setAlgoForm(prev => ({ ...prev, paper: e.target.checked }))}
                      style={styles.toggleInput}
                    />
                    <span style={styles.toggleSlider} />
                    <span style={styles.toggleText}>{algoForm.paper ? 'Paper Trading' : 'Live Trading'}</span>
                  </label>
                </div>
              </div>
            </div>

            <div style={styles.expandHint}>
              Scroll down for more advanced settings...
            </div>
          </div>
        </Card>


      </div>
    </div>
  );
};

const styles = {
  container: {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: theme.spacing[4],
    fontFamily: theme.typography.fontFamily.sans.join(', '),
    backgroundColor: 'var(--bg-secondary)',
    minHeight: '100vh',
  },
  heroSection: {
    background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
    borderRadius: theme.borderRadius['2xl'],
    padding: theme.spacing[8],
    marginBottom: theme.spacing[8],
    color: 'white',
    position: 'relative',
    overflow: 'hidden',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  },
  heroOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'radial-gradient(circle at top right, rgba(255,255,255,0.1) 0%, transparent 60%)',
  },
  heroContent: {
    position: 'relative',
    zIndex: 1,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing[4],
  },
  heroHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing[4],
  },
  heroIcon: {
    fontSize: '3.5rem',
    filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.1))',
  },
  heroTitle: {
    fontSize: theme.typography.fontSize['4xl'],
    fontWeight: theme.typography.fontWeight.bold,
    margin: 0,
    letterSpacing: '-0.025em',
  },
  heroSubtitle: {
    fontSize: theme.typography.fontSize.lg,
    color: 'white',
    opacity: 0.95,
    margin: '4px 0 0 0',
    fontWeight: theme.typography.fontWeight.medium,
  },
  statusBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing[2],
    padding: `${theme.spacing[2]} ${theme.spacing[4]}`,
    borderRadius: theme.borderRadius.full,
    border: '1px solid',
    fontWeight: theme.typography.fontWeight.bold,
    fontSize: theme.typography.fontSize.sm,
    letterSpacing: '0.05em',
    backdropFilter: 'blur(8px)',
  },
  statusDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing[6],
  },
  mainGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
    gap: theme.spacing[6],
  },
  controlCard: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  controlActions: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing[3],
  },
  controlButton: {
    width: '100%',
    padding: theme.spacing[4],
    fontSize: theme.typography.fontSize.lg,
    fontWeight: theme.typography.fontWeight.bold,
    letterSpacing: '0.05em',
    borderRadius: theme.borderRadius.xl,
    border: 'none',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  },
  metricsCard: {
    display: 'flex',
    flexDirection: 'column',
  },
  metricsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: theme.spacing[4],
  },
  metricBox: {
    backgroundColor: 'var(--bg-tertiary)',
    padding: theme.spacing[4],
    borderRadius: theme.borderRadius.xl,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px solid var(--border-primary)',
  },
  metricLabel: {
    fontSize: theme.typography.fontSize.xs,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: 'var(--text-secondary)',
    marginBottom: theme.spacing[1],
  },
  metricValue: {
    fontSize: theme.typography.fontSize['2xl'],
    fontWeight: theme.typography.fontWeight.bold,
    color: 'var(--text-primary)',
  },
  configCard: {
    overflow: 'hidden',
  },
  configHeader: {
    padding: theme.spacing[6],
    borderBottom: '1px solid var(--border-primary)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing[4],
  },
  configTitle: {
    fontSize: theme.typography.fontSize.xl,
    fontWeight: theme.typography.fontWeight.bold,
    margin: 0,
    color: 'var(--text-primary)',
  },
  tabs: {
    display: 'flex',
    gap: theme.spacing[2],
    backgroundColor: 'var(--bg-tertiary)',
    padding: theme.spacing[1],
    borderRadius: theme.borderRadius.lg,
  },
  tabButton: {
    padding: `${theme.spacing[2]} ${theme.spacing[4]}`,
    borderRadius: theme.borderRadius.md,
    border: 'none',
    background: 'transparent',
    color: 'var(--text-secondary)',
    fontWeight: theme.typography.fontWeight.medium,
    cursor: 'pointer',
    fontSize: theme.typography.fontSize.sm,
  },
  configContent: {
    padding: theme.spacing[6],
  },
  configGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: theme.spacing[6],
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing[2],
  },
  label: {
    fontSize: theme.typography.fontSize.sm,
    fontWeight: theme.typography.fontWeight.semibold,
    color: 'var(--text-secondary)',
  },
  modernInput: {
    padding: theme.spacing[3],
    backgroundColor: 'var(--bg-tertiary)',
    border: '1px solid var(--border-primary)',
    borderRadius: theme.borderRadius.lg,
    color: 'var(--text-primary)',
    fontSize: theme.typography.fontSize.base,
    outline: 'none',
    transition: 'border-color 0.2s ease',
  },
  modernSelect: {
    padding: theme.spacing[3],
    backgroundColor: 'var(--bg-tertiary)',
    border: '1px solid var(--border-primary)',
    borderRadius: theme.borderRadius.lg,
    color: 'var(--text-primary)',
    fontSize: theme.typography.fontSize.base,
    outline: 'none',
    cursor: 'pointer',
  },
  toggleContainer: {
    display: 'flex',
    alignItems: 'center',
    height: '48px', // Match input height
  },
  toggleLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing[3],
    cursor: 'pointer',
  },
  toggleInput: {
    display: 'none',
  },
  toggleSlider: {
    width: '48px',
    height: '24px',
    backgroundColor: 'var(--bg-tertiary)',
    borderRadius: '12px',
    position: 'relative',
    border: '1px solid var(--border-primary)',
    transition: 'background-color 0.2s ease',
  },
  toggleText: {
    fontSize: theme.typography.fontSize.base,
    fontWeight: theme.typography.fontWeight.medium,
    color: 'var(--text-primary)',
  },
  expandHint: {
    textAlign: 'center',
    marginTop: theme.spacing[6],
    color: 'var(--text-tertiary)',
    fontSize: theme.typography.fontSize.sm,
    fontStyle: 'italic',
  },
  terminalCard: {
    backgroundColor: '#1e1e1e',
    color: '#e5e7eb',
    fontFamily: 'monospace',
    overflow: 'hidden',
  },
  terminalHeader: {
    padding: `${theme.spacing[3]} ${theme.spacing[4]}`,
    backgroundColor: '#2d2d2d',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: '1px solid #3d3d3d',
  },
  terminalTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing[2],
    fontSize: theme.typography.fontSize.sm,
    fontWeight: theme.typography.fontWeight.bold,
  },
  terminalIcon: {
    color: '#10b981',
  },
  clearButton: {
    background: 'transparent',
    border: 'none',
    color: '#9ca3af',
    cursor: 'pointer',
    fontSize: theme.typography.fontSize.xs,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  terminalBody: {
    padding: theme.spacing[4],
    height: '300px',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing[1],
  },
  emptyTerminal: {
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#4b5563',
  },
  logLine: {
    display: 'flex',
    gap: theme.spacing[2],
    fontSize: '13px',
    lineHeight: '1.5',
  },
  logTime: {
    color: '#6b7280',
    minWidth: '85px',
  },
  authPrompt: {
    textAlign: 'center',
    padding: theme.spacing[16],
  },
  authTitle: {
    fontSize: theme.typography.fontSize['2xl'],
    fontWeight: theme.typography.fontWeight.bold,
    marginBottom: theme.spacing[4],
  },
  authMessage: {
    color: 'var(--text-secondary)',
  },
};

export default AdvancedAlgoRunnerPage;
